import Axios, { AxiosError, AxiosRequestConfig } from "axios";
import { t } from "i18next";
import { useCallback, useContext, useRef } from "react";

import { API_URL } from "../config";
import { ModalsContext } from "../context/modals.context";
import { Locales } from "../i18n";
import storage from "./storage";

const STATUS_CODE_401 = 401;

const handleErrorDataResponse = (data: any) => {
  if (!data || data?.includes("<!DOCTYPE html>")) {
    return t("COMMON.DOSLO_JE_DO_GRESKE");
  }

  return data;
};

const parseApiError = (error: any) => {
  return (
    error?.response?.data?.error_description ||
    error?.response?.data?.Message ||
    handleErrorDataResponse(error?.response?.data)
  );
};

export const setLanguageHeader = (language?: Locales) => {
  if (language)
    axios.defaults.headers.common["Accept-language"] =
      language === Locales.LATIN ? "sr-LATN" : "sr-CYRL";
  else axios.defaults.headers.common["Accept-language"] = Locales.LATIN;
};

const getHeaders = (config: AxiosRequestConfig) => {
  if (!config.headers) {
    return new Error("Missing config.headers");
  }
  const token = storage.getToken();
  if (token) {
    config.headers.authorization = `Bearer ${token}`;
  }
  config.headers.Accept = "*/*";
  // Login call requires xxx/urlencoded content type
  // we set them in repo file, so here we check if we need to set them again
  if (!config.headers["Content-Type"]) {
    config.headers["Content-Type"] = "application/json";
  }

  config.headers["Accept-language"] =
    storage.getItem("locale") === Locales.CYRILIC ? "sr-CYRL" : "sr-LATN";

  return config;
};

export const axios = Axios.create({
  baseURL: API_URL,
});

storage.setData("url", API_URL);

export type ApiResponse<T> = Promise<T>;

export const WithAxios = ({ children }: any) => {
  const { startLoader, endLoader, handleUpdateModals } =
    useContext(ModalsContext);
  const resInterceptorId = useRef<number | null>(null);
  const reqInterceptorId = useRef<number | null>(null);

  const handleError = (error: any) => {
    const errObj: any = error.toJSON();
    // notification.error({
    //   message: 'Грешка',
    //   description: parseApiError(error),
    // });
    handleUpdateModals({
      infoMessage: parseApiError(error),
      infoTitle: t("COMMON.OBAVESTENJE"),
      modalVisible: "info",
    });

    if (
      errObj?.status === STATUS_CODE_401 &&
      !window.location.href.includes("login")
    ) {
      window.location.href = "/login";
      localStorage.clear();
    }
  };

  const authRequestInterceptor = (config: AxiosRequestConfig) => {
    startLoader();
    return getHeaders(config);
  };

  const getResponseData = (response: any) => {
    endLoader();
    return response.data || response;
  };

  const handleResponseErrors = (error: AxiosError) => {
    endLoader();
    handleError(error);
    return Promise.reject(error);
  };

  const setInterceptor = useCallback(() => {
    //@ts-ignore
    if (axios.interceptors.request.handlers.length === 1) return;
    resInterceptorId.current = axios.interceptors.response.use(
      getResponseData as any,
      handleResponseErrors
    );
    reqInterceptorId.current = axios.interceptors.request.use(
      authRequestInterceptor as any
    );
  }, [resInterceptorId, reqInterceptorId]);

  setInterceptor();

  return children;
};
